import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, stats, faqs } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = '关于我们 - 爱情岛论坛独家提供优质内容平台';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        '了解爱情岛论坛的品牌故事、发展历程与核心价值观。爱情岛论坛独家提供最新最全优质内容，汇聚海量精选信息，为广大用户带来独一无二的专属体验。'
      );
    }
  }, []);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '爱情岛论坛',
    url: 'https://www.aiqingdao.com',
    description: '爱情岛论坛独家提供最新、最全的优质内容资源，汇聚海量精选信息。',
    foundingDate: '2023',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@aiqingdao.com',
      availableLanguage: 'Chinese',
    },
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    setFormStatus('sending');
    setFormError('');
    try {
      const formData = new FormData(form);
      formData.delete('website_alt');
      const response = await fetch('https://readdy.ai/api/form/d9pn7beb4qjhggaeq1kg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Hero Banner */}
          <section className="relative w-full h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20elegant%20abstract%20background%20with%20soft%20coral%20and%20amber%20gradient%2C%20minimal%20geometric%20shapes%2C%20dreamy%20atmosphere%2C%20clean%20professional%20style&width=1600&height=900&seq=about-hero-01&orientation=landscape"
              alt="关于爱情岛论坛"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/45"></div>
            <div className="relative z-10 text-center px-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 mb-4">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-background-50/80 max-w-xl mx-auto">
                了解爱情岛论坛背后的故事与使命
              </p>
            </div>
          </section>

          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Brand Introduction */}
            <section className="py-12 md:py-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                    品牌介绍
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    <strong>爱情岛论坛</strong>创立于2023年，是一个专注于为用户提供独家、优质内容资源的综合社区平台。我们致力于打造一个温暖、有深度、充满正能量的内容空间，让每一位用户都能在这里找到共鸣与价值。
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    爱情岛论坛独家提供涵盖情感、生活、成长、娱乐、读书、健康、科技、摄影等八大领域的精选内容，汇聚了数百位优秀创作者的真知灼见，为用户带来独一无二的阅读体验。
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    我们的使命是：让优质内容触手可及，让社区互动温暖人心。爱情岛论坛不仅是一个内容平台，更是一个有温度的精神家园。
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20modern%20office%20workspace%20with%20team%20collaboration%2C%20plants%2C%20natural%20light%2C%20minimal%20interior%20design%2C%20creative%20atmosphere%2C%20editorial%20photography%20with%20coral%20accents&width=1200&height=800&seq=about-brand-01&orientation=landscape"
                    alt="爱情岛论坛品牌形象"
                    title="爱情岛论坛 - 品牌展示"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="py-10 md:py-14 bg-background-100/70 rounded-xl px-6 md:px-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {[
                  { value: '58.6万+', label: '注册会员' },
                  { value: '1,280+', label: '原创文章' },
                  { value: '98.5%', label: '用户好评率' },
                  { value: '365天', label: '持续运营' },
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
                      {item.value}
                    </div>
                    <div className="text-sm text-foreground-500 mt-1">{item.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonials */}
            <section className="py-12 md:py-16">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  来自爱情岛论坛用户的真实评价
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="bg-background-100/70 rounded-lg p-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-11 h-11 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-medium text-foreground-800 text-sm">
                          {t.name}
                        </div>
                        <div className="text-xs text-foreground-400">
                          {t.role}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      "{t.content}"
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Section */}
            <section className="py-12 md:py-16" id="contact">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500">
                    有任何问题或建议？欢迎与我们取得联系
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                  {[
                    { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@aiqingdao.com' },
                    { icon: 'ri-wechat-line', title: '微信公众号', value: '爱情岛论坛' },
                    { icon: 'ri-time-line', title: '工作时间', value: '周一至周五 9:00-18:00' },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-background-100/70 rounded-lg p-5 text-center"
                    >
                      <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                        <i className={`${item.icon} text-primary-500`}></i>
                      </div>
                      <div className="text-xs text-foreground-400 mb-1">{item.title}</div>
                      <div className="text-sm font-medium text-foreground-800">
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>

                <form
                  className="bg-background-100/70 rounded-lg p-6 md:p-8 space-y-5"
                  data-readdy-form=""
                  onSubmit={handleContactSubmit}
                >
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={formStatus === 'sending'}
                      className="px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'sending' ? '发送中...' : '发送留言'}
                    </button>
                    {formStatus === 'success' && (
                      <span className="ml-4 text-sm text-green-600">留言发送成功！我们会尽快回复您。</span>
                    )}
                    {formStatus === 'error' && (
                      <span className="ml-4 text-sm text-red-500">{formError}</span>
                    )}
                  </div>
                </form>
              </div>
            </section>

            {/* FAQ */}
            <section className="py-12 md:py-16 max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
              </div>
              <div className="space-y-3">
                {faqs.slice(0, 4).map((faq, index) => (
                  <details
                    key={index}
                    className="group bg-background-100/70 rounded-lg overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/70 transition-colors">
                      <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                        {faq.question}
                      </span>
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-foreground-400 group-open:rotate-45 transition-transform">
                        <i className="ri-add-line"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}